import { createContext, useContext, useState, useEffect, useMemo, useRef, ReactNode } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { localeForLanguage } from '@dental-manager/shared';
import { api } from '../../lib/api';
import { setCurrencyContext, currencySymbol } from '../../lib/format';
import { setCurrencySymbol } from '../../lib/i18n/i18n';

function parseSiteAdmin(token: string): boolean {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.isSiteAdmin === true;
  } catch {
    return false;
  }
}

function parseAccountType(token: string): string {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.accountType || 'full';
  } catch {
    return 'full';
  }
}

function parseIsOwner(token: string): boolean {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.isOwner === true;
  } catch {
    return false;
  }
}

// Types
export interface User {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  role: string;
  prefix?: string | null;
  displayName?: string | null;
  officeName?: string | null;
  officeCity?: string | null;
  phone?: string | null;
  avatarUrl?: string | null;
  profileCompleted?: boolean;
  isOwner?: boolean;
}

export interface Account {
  id: string;
  name: string;
  slug: string;
  plan: string;
  enabledModules: string[];
  entityType?: string;
  isDemo?: boolean;
  country?: string;
  currency?: string;
}

interface AuthContextType {
  user: User | null;
  account: Account | null;
  accounts: Account[];
  permissions: Record<string, any>;
  isAuthenticated: boolean;
  isSiteAdmin: boolean;
  isLoading: boolean;
  error: string | null;
  entityType: string;
  isGuest: boolean;
  isOwner: boolean;
  isDemo: boolean;
  login: (email: string, password: string) => Promise<void>;
  loginWithTokens: (data: { user: any; account: any; tokens: { accessToken: string; refreshToken: string } }) => void;
  register: (data: RegisterData) => Promise<void>;
  logout: () => Promise<void>;
  switchAccount: (accountId: string) => Promise<void>;
  clearError: () => void;
  refreshUser: () => Promise<void>;
  updateUserLocally: (data: Partial<User>) => void;
  updateAccountLocally: (data: Partial<Account>) => void;
}

export interface RegisterData {
  email: string;
  password: string;
  firstName?: string;
  lastName?: string;
  accountName: string;
  invitationToken?: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { t, i18n } = useTranslation('auth');
  const [user, setUser] = useState<User | null>(null);
  const [account, setAccount] = useState<Account | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [permissions, setPermissions] = useState<Record<string, any>>({});
  const [isSiteAdmin, setIsSiteAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const previousSymbol = useRef<string>('€');

  // Keep number/currency formatting in sync with the cabinet and the UI language
  useEffect(() => {
    setCurrencyContext(account?.currency, localeForLanguage(i18n.language));
    const symbol = currencySymbol();
    if (symbol !== previousSymbol.current) {
      previousSymbol.current = symbol;
      setCurrencySymbol(symbol);
      // Les libellés {{currency}} déjà rendus doivent se rafraîchir
      i18n.emit('languageChanged', i18n.language);
    }
  }, [account?.currency, i18n.language]);

  // Check for existing session on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = api.getAccessToken();
      if (!token) {
        setIsLoading(false);
        return;
      }

      try {
        const siteAdmin = parseSiteAdmin(token);
        if (siteAdmin) {
          // Site admin — don't need getMe accounts, just restore user from token
          const payload = JSON.parse(atob(token.split('.')[1]));
          setUser({ id: payload.userId, email: payload.email, firstName: null, lastName: null, role: 'site_admin' });
          setIsSiteAdmin(true);
          setIsLoading(false);
          return;
        }

        const response = await api.getMe();

        if (response.success && response.data) {
          const { user: userData, accounts: userAccounts, currentAccount } = response.data;

          const currentToken = api.getAccessToken();
          setUser({
            id: userData.id,
            email: userData.email,
            firstName: userData.first_name,
            lastName: userData.last_name,
            avatarUrl: (userData as any).avatar_url || null,
            role: userAccounts.find(a => a.id === currentAccount)?.role || 'assistant',
            profileCompleted: userData.profile_completed ?? true,
            isOwner: currentToken ? parseIsOwner(currentToken) : false,
          });

          const mappedAccounts = userAccounts.map(a => ({
            id: a.id,
            name: a.name,
            slug: a.slug,
            plan: 'free',
            enabledModules: a.enabled_modules,
            entityType: (a as any).entity_type || 'cabinet',
            isDemo: (a as any).is_demo || false,
            country: (a as any).country || undefined,
            currency: (a as any).currency || undefined,
          }));

          setAccounts(mappedAccounts);
          const activeAccount = mappedAccounts.find(a => a.id === currentAccount) || mappedAccounts[0];
          setAccount(activeAccount);
          setCurrencyContext(activeAccount?.currency, localeForLanguage(i18n.language));

          // Load permissions for current account
          const currentAccData = userAccounts.find(a => a.id === currentAccount);
          setPermissions((currentAccData as any)?.permissions || {});
        } else {
          api.clearTokens();
        }
      } catch (err) {
        console.error('Auth check failed:', err);
        api.clearTokens();
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = async (email: string, password: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await api.login(email, password);

      if (response.success && response.data) {
        const { user: userData, account: accountData, tokens } = response.data;

        api.setTokens(tokens.accessToken, tokens.refreshToken);

        const siteAdmin = parseSiteAdmin(tokens.accessToken);
        setIsSiteAdmin(siteAdmin);

        const loginPayload = JSON.parse(atob(tokens.accessToken.split('.')[1]));
        setUser({
          id: userData.id,
          email: userData.email,
          firstName: userData.first_name,
          lastName: userData.last_name,
          role: siteAdmin ? 'site_admin' : (loginPayload.role || 'admin'),
          isOwner: loginPayload.isOwner === true,
        });

        if (!siteAdmin) {
          const mappedAccount: Account = {
            id: accountData.id,
            name: accountData.name,
            slug: accountData.slug,
            plan: accountData.plan,
            enabledModules: accountData.enabled_modules,
            entityType: (accountData as any).entity_type || 'cabinet',
            isDemo: (accountData as any).is_demo || false,
            country: (accountData as any).country || undefined,
            currency: (accountData as any).currency || undefined,
          };
          setAccount(mappedAccount);
          setAccounts([mappedAccount]);
          setCurrencyContext(mappedAccount.currency, localeForLanguage(i18n.language));
          setPermissions((response.data as any).permissions || {});
        }

      } else {
        throw new Error(response.error?.message || 'Login failed');
      }
    } catch (err: any) {
      const message = err.message || 'Une erreur est survenue';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const loginWithTokens = (data: { user: any; account: any; tokens: { accessToken: string; refreshToken: string } }) => {
    const { user: userData, account: accountData, tokens } = data;

    api.setTokens(tokens.accessToken, tokens.refreshToken);

    const siteAdmin = parseSiteAdmin(tokens.accessToken);
    setIsSiteAdmin(siteAdmin);

    const payload = JSON.parse(atob(tokens.accessToken.split('.')[1]));
    setUser({
      id: userData.id,
      email: userData.email,
      firstName: userData.first_name,
      lastName: userData.last_name,
      role: siteAdmin ? 'site_admin' : (payload.role || 'admin'),
      isOwner: payload.isOwner === true,
    });

    if (!siteAdmin) {
      const mappedAccount: Account = {
        id: accountData.id,
        name: accountData.name,
        slug: accountData.slug,
        plan: accountData.plan,
        enabledModules: accountData.enabled_modules,
        entityType: (accountData as any).entity_type || 'cabinet',
        isDemo: (accountData as any).is_demo || false,
        country: (accountData as any).country || undefined,
        currency: (accountData as any).currency || undefined,
      };
      setAccount(mappedAccount);
      setAccounts([mappedAccount]);
      setCurrencyContext(mappedAccount.currency, localeForLanguage(i18n.language));
    }
  };

  const register = async (data: RegisterData) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await api.register({
        email: data.email,
        password: data.password,
        firstName: data.firstName,
        lastName: data.lastName,
        accountName: data.accountName,
        invitationToken: data.invitationToken,
      });

      if (response.success && response.data) {
        const { user: userData, account: accountData, tokens } = response.data;

        api.setTokens(tokens.accessToken, tokens.refreshToken);

        setUser({
          id: userData.id,
          email: userData.email,
          firstName: userData.first_name,
          lastName: userData.last_name,
          role: 'admin',
          isOwner: true,
        });

        const mappedAccount: Account = {
          id: accountData.id,
          name: accountData.name,
          slug: accountData.slug,
          plan: 'free',
          enabledModules: accountData.enabled_modules,
          entityType: 'cabinet',
          isDemo: false,
        };

        setAccount(mappedAccount);
        setAccounts([mappedAccount]);
      } else {
        throw new Error(response.error?.message || 'Registration failed');
      }
    } catch (err: any) {
      const message = err.message || 'Une erreur est survenue';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      await api.logout();
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setUser(null);
      setAccount(null);
      setAccounts([]);
      setPermissions({});
      setIsSiteAdmin(false);
      toast.info(t('toast.loggedOut'));
    }
  };

  const switchAccount = async (accountId: string) => {
    try {
      const response = await api.switchContext(accountId);
      if (response.success && response.data) {
        const { account: accountData, tokens } = response.data;
        api.setTokens(tokens.accessToken, tokens.refreshToken);

        const mappedAccount: Account = {
          id: accountData.id,
          name: accountData.name,
          slug: accountData.slug,
          plan: accountData.plan,
          enabledModules: accountData.enabled_modules,
          entityType: accountData.entity_type || 'cabinet',
          isDemo: (accountData as any).is_demo || false,
          country: (accountData as any).country || undefined,
          currency: (accountData as any).currency || undefined,
        };
        setAccount(mappedAccount);
        setCurrencyContext(mappedAccount.currency, localeForLanguage(i18n.language));
        setPermissions((response.data as any).permissions || {});

        // Update user role and isOwner from JWT
        const payload = JSON.parse(atob(tokens.accessToken.split('.')[1]));
        setUser(prev => prev ? { ...prev, role: payload.role, isOwner: payload.isOwner === true } : prev);

        toast.success(t('toast.contextChanged', { name: accountData.name }));
      }
    } catch (err) {
      console.error('Switch context error:', err);
      toast.error(t('toast.contextChangeError'));
    }
  };

  const refreshUser = async () => {
    try {
      const response = await api.getMe();
      if (response.success && response.data) {
        const { user: userData, accounts: userAccounts, currentAccount } = response.data;
        setUser(prev => ({
          ...prev!,
          id: userData.id,
          email: userData.email,
          firstName: userData.first_name,
          lastName: userData.last_name,
          role: userAccounts.find(a => a.id === currentAccount)?.role || prev?.role || 'assistant',
        }));
      }
    } catch {
      // silent
    }
  };

  const updateUserLocally = (data: Partial<User>) => {
    setUser(prev => prev ? { ...prev, ...data } : prev);
  };

  const updateAccountLocally = (data: Partial<Account>) => {
    setAccount(prev => prev ? { ...prev, ...data } : prev);
    setAccounts(prev => prev.map(a => (a.id === account?.id ? { ...a, ...data } : a)));
  };

  const clearError = () => setError(null);

  const entityType = account?.entityType || 'cabinet';
  const token = api.getAccessToken();
  const isGuest = token ? parseAccountType(token) === 'guest' : false;
  const isOwner = user?.isOwner || false;
  const isDemo = account?.isDemo || false;

  const contextValue = useMemo(() => ({
    user,
    account,
    accounts,
    permissions,
    isAuthenticated: !!user,
    isSiteAdmin,
    isLoading,
    error,
    entityType,
    isGuest,
    isOwner,
    isDemo,
    login,
    loginWithTokens,
    register,
    logout,
    switchAccount,
    clearError,
    refreshUser,
    updateUserLocally,
    updateAccountLocally,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [user, account, accounts, permissions, isSiteAdmin, isLoading, error, entityType, isGuest, isOwner, isDemo]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
